import { useEffect, useState } from "react";
import { Mail, MapPin, Globe, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHero } from "../components/ui-bits";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", vertical: "", message: "" });
  const [verticals, setVerticals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/verticals").then((r) => setVerticals(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      setDone(true);
      toast.success(data.message || "Message sent!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get in Touch"
        title="Let's start a conversation."
        subtitle="For partnerships, investments, media or general enquiries — our team is ready to help."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="space-y-6">
          <div className="card-vertical rounded-2xl p-6">
            <Mail className="w-6 h-6 text-emerald-400 mb-3" />
            <h3 className="font-display font-semibold text-white">Email</h3>
            <a href="mailto:info@srammram.com" className="text-slate-400 text-sm hover:text-emerald-400" data-testid="contact-email-link">info@srammram.com</a>
          </div>
          <div className="card-vertical rounded-2xl p-6">
            <MapPin className="w-6 h-6 text-emerald-400 mb-3" />
            <h3 className="font-display font-semibold text-white">Offices</h3>
            <p className="text-slate-400 text-sm">Kuala Lumpur · Dubai · Qatar · UK · India</p>
          </div>
          <div className="card-vertical rounded-2xl p-6">
            <Globe className="w-6 h-6 text-emerald-400 mb-3" />
            <h3 className="font-display font-semibold text-white">Global Reach</h3>
            <p className="text-slate-400 text-sm">Operating across the Middle East, UK and Europe.</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card-vertical rounded-2xl p-8">
            {done ? (
              <div className="text-center py-16" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-2xl font-display font-bold text-white mb-2">Message sent</h3>
                <p className="text-slate-400">Thank you for reaching out. Our team will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm text-slate-400 mb-1.5 block">Name *</label>
                    <input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1.5 block">Email *</label>
                    <input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm text-slate-400 mb-1.5 block">Subject</label>
                    <input data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1.5 block">Vertical of Interest</label>
                    <select data-testid="contact-vertical-select" value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                      className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50">
                      <option value="">Select a vertical</option>
                      {verticals.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1.5 block">Message *</label>
                  <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit-btn"
                  className="btn-emerald px-7 py-3.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
                  <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
